import type { StatusFilter } from '@/store/rate-propagation.store'
import { useState } from 'react'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { formatCount } from '@/lib/utils/format-count'
import { useRatePropagationStore } from '@/store/rate-propagation.store'
import { ArrowsClockwiseIcon } from '@phosphor-icons/react'

type Choice = 'draft' | 'submitted' | 'both' | 'none'

/**
 * One choice row. The dialog grammar wants a quiet fill rather than an
 * outline, so the selected option deepens instead of drawing a border.
 */
const IMPACT_OPTION_CLASS =
  'flex cursor-pointer items-start gap-3 rounded-md bg-muted/30 p-3 transition-colors hover:bg-muted/40 has-[[data-state=checked]]:bg-primary/10'

export function RateImpactDialog() {
  const { impactDialogOpen, pendingAction, impactCounts, closeImpactDialog } =
    useRatePropagationStore()
  const [choice, setChoice] = useState<Choice | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const draftCount = impactCounts?.draftCount ?? 0
  const submittedCount = impactCounts?.submittedCount ?? 0
  const hasBoth = draftCount > 0 && submittedCount > 0

  const defaultChoice: Choice = hasBoth
    ? 'both'
    : draftCount > 0
      ? 'draft'
      : 'submitted'
  const effectiveChoice = choice ?? defaultChoice

  function handleOpenChange(open: boolean) {
    if (!open) handleSkip()
  }

  async function handleSkip() {
    closeImpactDialog()
    setChoice(null)
    await pendingAction?.(['CHANGES_REQUESTED', 'REJECTED'])
  }

  async function handleConfirm() {
    if (!pendingAction) return
    setIsSubmitting(true)
    closeImpactDialog()
    setChoice(null)

    const statusFilter: StatusFilter = ['CHANGES_REQUESTED', 'REJECTED']
    if (effectiveChoice === 'draft' || effectiveChoice === 'both')
      statusFilter.push('DRAFT')
    if (effectiveChoice === 'submitted' || effectiveChoice === 'both')
      statusFilter.push('SUBMITTED')

    await pendingAction(statusFilter)
    setIsSubmitting(false)
  }

  if (!impactDialogOpen) return null

  return (
    <DialogPattern
      open={impactDialogOpen}
      onOpenChange={handleOpenChange}
      title="Rate change affects existing entries"
      description="This rate update affects time entries that haven't been approved yet. Choose which entries should have their rates recalculated."
      icon={
        <ArrowsClockwiseIcon
          className="h-4 w-4 text-primary"
          weight="duotone"
        />
      }
      submitLabel="Apply"
      cancelLabel="Skip updates"
      onSubmit={handleConfirm}
      onCancel={handleSkip}
      isSubmitting={isSubmitting}
      size="md"
    >
      <RadioGroup
        value={effectiveChoice}
        onValueChange={v => setChoice(v as Choice)}
        className="gap-2"
      >
        {draftCount > 0 && (
          <label className={IMPACT_OPTION_CLASS}>
            <RadioGroupItem
              value="draft"
              id="impact-draft"
              className="mt-0.5"
            />
            <div className="min-w-0 flex-1">
              <Label
                htmlFor="impact-draft"
                className="cursor-pointer font-medium"
              >
                Update draft entries
              </Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {draftCount} draft {draftCount === 1 ? 'entry' : 'entries'}{' '}
                within the effective date range
              </p>
            </div>
          </label>
        )}

        {submittedCount > 0 && (
          <label className={IMPACT_OPTION_CLASS}>
            <RadioGroupItem
              value="submitted"
              id="impact-submitted"
              className="mt-0.5"
            />
            <div className="min-w-0 flex-1">
              <Label
                htmlFor="impact-submitted"
                className="cursor-pointer font-medium"
              >
                Update submitted entries
              </Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {submittedCount} submitted{' '}
                {submittedCount === 1 ? 'entry' : 'entries'} within the
                effective date range
              </p>
            </div>
          </label>
        )}

        {hasBoth && (
          <label className={IMPACT_OPTION_CLASS}>
            <RadioGroupItem value="both" id="impact-both" className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <Label
                htmlFor="impact-both"
                className="cursor-pointer font-medium"
              >
                Update both
              </Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {formatCount(draftCount + submittedCount, 'entry', 'entries')}{' '}
                total
              </p>
            </div>
          </label>
        )}

        <label className={IMPACT_OPTION_CLASS}>
          <RadioGroupItem value="none" id="impact-none" className="mt-0.5" />
          <div className="min-w-0 flex-1">
            <Label htmlFor="impact-none" className="cursor-pointer font-medium">
              Don't update existing entries
            </Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Only new entries after this point will use the updated rate
            </p>
          </div>
        </label>
      </RadioGroup>
    </DialogPattern>
  )
}
